// hooks/useInitialDataLoader.tsx
import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  getApiProducts,
  getQuote,
  getQuoteSummary,
  getProduct1,
  getBranding,
  configureAPIs,
} from '../apiCalls';
import { DeploymentHost, TBranding, TJourneyConfig, TJourneyMode, TProductData, TQuote } from '../types';
import { DEFAULT_UUID } from '../apiCalls';

import productTester from '../journeyConfigs/no-id/Product Tester.json';
import PBUIFields from '../journeyConfigs/no-id/PBUIFieldsPreview.json';
import { allJourneyConfigs, getJourneyById } from '../journeyConfigs';


const isFieldsPreview = new URL(window.location.href)?.searchParams?.get('preview_fields');

export const useInitialDataLoader = ({
  envParams,
  uuid,
  fetchedBranding,
  setJourneys,
  setJourneyMode,
  setError,
  setProductData,
  updateProductData,
  setActiveJourney,
  setProductsLoading,
  handleBranding,
}: {
  envParams: any;
  uuid: string;
  fetchedBranding: TBranding;
  setJourneys: (v: TProductData[]) => void;
  setJourneyMode: Dispatch<SetStateAction<TJourneyMode>>;
  setError: (v: { title: string; content: string }) => void;
  setProductData: (v: TProductData) => void;
  updateProductData: (v: TProductData) => void;
  setActiveJourney: (v: TJourneyConfig) => void;
  setProductsLoading: (v: boolean) => void;
  handleBranding: (branding: TBranding, renderVersion: 'V1' | 'V2') => boolean;
}) => {
  useEffect(() => {
    const fetchInitialData = async () => {
      const { userSessionToken, quoteId, productId, hostname, mode } = envParams;

      const productsFromDynamoDb = await getApiProducts(uuid, fetchedBranding?.slug);
      if (productsFromDynamoDb) setProductsLoading(false);

      configureAPIs(hostname as DeploymentHost, uuid);

      if (!uuid && !userSessionToken && !quoteId && mode !== 'EDIT') {
        return setJourneyModeWithError('No Parameters', `None of the required parameters are specified. Remember to add e.g. ?uuid=${DEFAULT_UUID} to the URL.`);
      }

      if (userSessionToken) {
        return setJourneyMode('SUCCESS');
      }

      if (quoteId) {
        const summary = await getQuoteSummary(quoteId);
        if (!summary) return setJourneyModeWithError('Problem Fetching Data', 'The quote summary could not be fetched.');

        const quote = await getQuote(quoteId);
        if (!quote) return setJourneyModeWithError('Problem Fetching Data', 'The quote could not be fetched.');

        configureAPIs(hostname as DeploymentHost, quote.mainBrokerId);

        const product = await getProduct1(quote.productId, productsFromDynamoDb.data);
        if (!product || !product.journeyConfig) return setJourneyModeWithError('Product Unavailable.', 'Sorry. This product is not available.');

        const branding = fetchedBranding ?? (await getBranding(quote.mainBrokerId));
        if (!branding) return setJourneyModeWithError('Problem Fetching Data', 'The branding could not be fetched.');

        const isValid = handleBranding(branding, product.journeyConfig.renderVersion);
        if (!isValid) return setJourneyModeWithError('Unexpected branding response', 'The branding response is invalid or incomplete.');

        setProductData(product);
        updateProductData(product);

        const initialValues = {
          ...quote.quoteRequest.originalData.formValues,
          paymentIntent: quote.paymentDetails,
          quote: { ...summary, useNewMtaQp: quote?.useNewMtaQp },
        };
        setActiveJourney({ ...product.journeyConfig, initialValues });

        const stateMap: Record<string, TJourneyMode> = {
          QUOTE: 'RESUME',
          BOUND: 'MTA',
          REFERRED: 'MTA',
        };

        const stateErrors: Record<TQuote['state'], string> = {
          EXPIRED: `Quote has expired and cannot be worked on.`,
          CANCELLED: `Quote has been cancelled.`,
          DEFAULT: `Quote in state ${quote.state} cannot be worked on.`,
        };

        if (quote.state in stateErrors) {
          return setJourneyModeWithError('Invalid Quote State', stateErrors[quote.state] || stateErrors.DEFAULT);
        }

        return setJourneyMode(stateMap[quote.state] || 'ERROR');
      }

      const branding = fetchedBranding ?? (await getBranding(uuid));
      if (!branding) return setJourneyModeWithError('Problem Fetching Data', 'The branding could not be fetched.');

      const isValid = handleBranding(branding, 'V1'); // Default render version fallback
      if (!isValid) return setJourneyModeWithError('Unexpected branding response', 'The branding response is invalid or incomplete.');

      if (productId) {
        const product = await getProduct1(productId, productsFromDynamoDb.data);
        if (!product || product.state !== 'active' || !product.published) {
          return setJourneyModeWithError('Product Unavailable.', 'Sorry. This product is not available.');
        }

        setProductData(product);
        updateProductData(product);
        handleBranding(fetchedBranding ?? branding, product.journeyConfig?.renderVersion);
        setActiveJourney(product.journeyConfig);
        return setJourneyMode('RESUME');
      }

      setJourneys(productsFromDynamoDb.data);
      setJourneyMode('DEFAULT');
    };

    const setJourneyModeWithError = (title: string, content: string) => {
      setJourneyMode('ERROR');
      setError({ title, content });
    };

    switch (envParams.mode) {
      case 'EDIT':
        configureAPIs(envParams.hostname as DeploymentHost);
        setJourneyMode('EDIT');
        setProductsLoading(false);
        break;
      case 'TEST':
        configureAPIs(envParams.hostname as DeploymentHost);
        setJourneys(allJourneyConfigs as TProductData[]);
        setJourneyMode('TEST');
        setActiveJourney((isFieldsPreview ? PBUIFields : productTester) as TJourneyConfig);
        setProductsLoading(false);
        break;
      default:
        fetchInitialData();
    }
  }, []);
};
